import { registrationState, vnDayMonth } from '@hbac/contract';
import { ArrowRight, ChevronDown, Megaphone } from 'lucide-react';
import { Suspense } from 'react';
import { Link } from 'react-router-dom';
import { publicPaths } from '../../routes/paths';
import { useNow } from '../../hooks/use-now';
import { lazyOptional } from '../../lib/lazy-optional';
import { usePublicSiteContent } from '../../services/public-queries';
import { SocialButtons } from '../components/SocialLinks';

// The school of dolphins (Plan AP) comes with its own small chunk, after the page.
const DolphinSchool = lazyOptional(() => import('../dolphins/DolphinSchool'));

export function Hero() {
  const { content } = usePublicSiteContent();
  const now = useNow();
  const r = content?.recruitment;
  // While a round is open (or about to open), a strip above the badge leads to /join.
  const round = r?.enabled ? registrationState(r.opensAt, r.closesAt, now) : null;
  // The first screen is the hero and nothing else: it fills the viewport below
  // the header, content centred, so the next section starts below the fold
  // rather than peeking in.
  return (
    <section id="hero" className="min-h-hero relative flex items-center overflow-hidden bg-gradient-to-br from-navy-900 via-navy-800 to-brand-800">
      {/* Decorative only — hidden from assistive tech and never carrying meaning. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-20">
        <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-brand-400 blur-3xl" />
        <div className="absolute -bottom-32 right-0 h-80 w-80 rounded-full bg-brand-500 blur-3xl" />
      </div>
      <Suspense fallback={null}>
        <DolphinSchool />
      </Suspense>

      {/* Text left, the members' photo right from lg. Below lg the photo is left
          out so the first screen stays one screen on phones and tablets. */}
      <div className="relative mx-auto grid w-full max-w-[1328px] items-center gap-12 px-4 py-16 md:px-6 md:py-20 lg:grid-cols-[1.05fr_1fr]">
        <div>
          {r && (round === 'open' || round === 'before') && (
            <Link
              to={publicPaths.join}
              data-testid="join-strip"
              className="mb-6 flex w-fit items-center gap-2 rounded-full bg-brand-400/15 py-1.5 pl-2 pr-3 text-sm font-semibold text-white ring-1 ring-brand-300/40 transition-colors hover:bg-brand-400/25"
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-400 text-navy-900">
                <Megaphone size={13} />
              </span>
              {round === 'open' ? `Đang tuyển thành viên ${r.season}, hạn nộp ${vnDayMonth(r.closesAt)}` : `Sắp mở đơn tuyển thành viên ${r.season} từ ${vnDayMonth(r.opensAt)}`}
              <ArrowRight size={14} />
            </Link>
          )}

          <h1 className="max-w-3xl text-balance text-[40px] font-extrabold leading-[1.08] tracking-[-0.02em] text-white sm:text-5xl md:text-6xl lg:text-[56px] xl:text-[68px]">
            Biến dữ liệu thành
            <span className="text-brand-300"> quyết định</span>
          </h1>

          <p className="mt-6 max-w-xl text-lg leading-relaxed text-brand-100/90 md:text-xl">
            Cộng đồng sinh viên Bách khoa yêu thích phân tích kinh doanh và khoa học dữ liệu.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <a
              href="#about"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-6 py-3.5 text-base font-bold text-navy-900 transition-transform hover:scale-[1.02] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
            >
              Tìm hiểu về CLB <ArrowRight size={16} />
            </a>
          </div>

          <div className="mt-6">
            <SocialButtons />
          </div>
        </div>

        <figure className="relative hidden lg:block">
          {/* <picture> with a media query, not just a hidden <img>: a hidden
              img still downloads, and phones never show this photo. */}
          <picture>
            <source
              media="(min-width: 1024px)"
              srcSet="/images/hero-members-800.webp 800w, /images/hero-members-1200.webp 1200w"
              sizes="(min-width: 1280px) 540px, 45vw"
            />
            <img
              src="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=="
              width={1200}
              height={800}
              alt="Thành viên HBAC trong buổi sinh hoạt chung của câu lạc bộ"
              fetchPriority="high"
              decoding="async"
              className="aspect-[3/2] w-full rounded-3xl object-cover shadow-2xl shadow-navy-950/40 ring-1 ring-white/15"
            />
          </picture>
        </figure>
      </div>

      <a
        href="#about"
        aria-label="Xem tiếp"
        className="hero-cue absolute bottom-5 left-1/2 flex h-10 w-10 -translate-x-1/2 items-center justify-center rounded-full text-white/50 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
      >
        <ChevronDown size={22} />
      </a>
    </section>
  );
}
